/**
 * @fileoverview ParametroTintaModal - Modal for adding/editing ink parameters
 */

import React, { useState, useEffect } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Form,
  FormGroup,
  Label,
  Input,
  Alert,
  Spinner,
} from 'reactstrap';
import { PantoneColorPicker } from './PantoneColorPicker';
import { Tinta, CreateParametrosTintaData, UpdateParametrosTintaData, ParametrosTintaExpanded } from '../types/collections';

interface ParametroTintaModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: CreateParametrosTintaData | UpdateParametrosTintaData) => void;
  tintas: Tinta[];
  parametro?: ParametrosTintaExpanded | null;
  isEdit: boolean;
  loading?: boolean;
  error?: string | null;
}

interface FormData {
  nombre: string;
  tinta: string[]; // Array of selected tinta IDs
  pantone: string;
  estado: boolean;
}

interface FormErrors {
  nombre?: string;
  tinta?: string;
  pantone?: string;
}

export const ParametroTintaModal: React.FC<ParametroTintaModalProps> = ({
  isOpen,
  onClose,
  onSave,
  tintas,
  parametro,
  isEdit,
  loading = false,
  error = null,
}) => {
  const [formData, setFormData] = useState<FormData>({
    nombre: '',
    tinta: [],
    pantone: '',
    estado: true,
  });

  const [errors, setErrors] = useState<FormErrors>({});
  const [showColorPicker, setShowColorPicker] = useState(false);
  const [pantoneHex, setPantoneHex] = useState<string | null>(null);
  const [lastSuggestedName, setLastSuggestedName] = useState<string | null>(null);

  const deriveSuggestedName = (pantoneStr: string): string => {
    if (!pantoneStr) return '';
    const withoutPrefix = pantoneStr.replace(/^PANTONE\s+/i, '');
    const compact = withoutPrefix.replace(/\s+/g, '').replace(/-/g, '');
    return compact.toUpperCase();
  };

  // Función para ordenar tintas: C, M, Y, K, luego el resto alfabéticamente
  const sortTintas = (tintas: Tinta[]): Tinta[] => {
    const order = ['C', 'M', 'Y', 'K'];
    return [...tintas].sort((a, b) => {
      const aIndex = order.indexOf(a.nombre);
      const bIndex = order.indexOf(b.nombre);
      
      // Si ambas están en el orden prioritario
      if (aIndex !== -1 && bIndex !== -1) {
        return aIndex - bIndex;
      }
      
      // Si solo 'a' está en el orden prioritario
      if (aIndex !== -1) {
        return -1;
      }
      
      // Si solo 'b' está en el orden prioritario
      if (bIndex !== -1) {
        return 1;
      }
      
      // Si ninguna está en el orden prioritario, ordenar alfabéticamente
      return a.nombre.localeCompare(b.nombre);
    });
  };

  // Reset form when modal opens/closes or when parametro changes
  useEffect(() => {
    if (isOpen) {
      if (isEdit && parametro) {
        setFormData({
          nombre: parametro.nombre || '',
          tinta: parametro.tinta || [],
          pantone: parametro.pantone || '',
          estado: parametro.estado ?? true,
        });
        // Initialize preview hex if stored pantone is a hex code
        const hexMatch = (parametro.pantone || '').match(/^#[0-9A-Fa-f]{6}$/);
        setPantoneHex(hexMatch ? hexMatch[0] : null);
        setLastSuggestedName(null);
      } else {
        setFormData({
          nombre: '',
          tinta: [],
          pantone: '',
          estado: true,
        });
        setPantoneHex(null);
        setLastSuggestedName(null);
      }
      setErrors({});
    }
  }, [isOpen, isEdit, parametro]);

  const handleInputChange = (field: keyof FormData, value: string | boolean | string[]) => {
    setFormData(prev => ({
      ...prev,
      [field]: value,
    }));

    // Update preview hex when user types a hex in pantone field
    if (field === 'pantone' && typeof value === 'string') {
      const hexMatch = value.match(/^#[0-9A-Fa-f]{6}$/);
      setPantoneHex(hexMatch ? hexMatch[0] : null);
    }

    // Clear error when user starts typing
    if (errors[field as keyof FormErrors]) {
      setErrors(prev => ({
        ...prev,
        [field]: undefined,
      }));
    }
  };

  const handleTintaChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const selectedOptions = Array.from(e.target.selectedOptions);
    const selectedValues = selectedOptions.map(option => option.value);
    setFormData(prev => ({
      ...prev,
      tinta: selectedValues,
    }));

    // Clear error when user starts typing
    if (errors.tinta) {
      setErrors(prev => ({
        ...prev,
        tinta: undefined,
      }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: FormErrors = {};

    // Validate nombre
    if (!formData.nombre.trim()) {
      newErrors.nombre = 'El nombre es requerido';
    } else if (formData.nombre.length > 255) {
      newErrors.nombre = 'El nombre es demasiado largo (máximo 255 caracteres)';
    }

    // Tinta is now optional - no validation needed

    // Validate pantone (optional but if provided, should be valid format)
    if (formData.pantone && !isValidPantoneFormat(formData.pantone)) {
      newErrors.pantone = 'Formato inválido (ej: PANTONE 186 C o #FF0000)';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const isValidPantoneFormat = (pantone: string): boolean => {
    // Allow both standard Pantone format and plain hex colors
    const pantoneRegex = /^PANTONE\s+\d+\s+[A-Z]$/i;
    const hexColorRegex = /^#[0-9A-Fa-f]{6}$/i;
    return pantoneRegex.test(pantone.trim()) || hexColorRegex.test(pantone.trim());
  };

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    const submitData = {
      nombre: formData.nombre.trim(),
      tinta: formData.tinta.length > 0 ? formData.tinta : [], // Send empty array if no selection
      pantone: formData.pantone.trim() || undefined,
      estado: formData.estado,
    };

    onSave(submitData);
  };

  const handleColorSelect = (color: { name: string; hex: string; pantone: string }) => {
    const suggested = deriveSuggestedName(color.name || color.pantone);
    setFormData(prev => {
      const userHasCustomName = !!(prev.nombre && prev.nombre.trim()) && prev.nombre !== lastSuggestedName;
      const nextNombre = userHasCustomName ? prev.nombre : suggested;
      // Track the last auto-suggested value so subsequent changes can replace it
      setLastSuggestedName(nextNombre);
      return {
        ...prev,
        pantone: color.pantone,
        nombre: nextNombre,
      };
    });
    setPantoneHex(color.hex);
    setShowColorPicker(false);
  };

  const handleClose = () => {
    if (!loading) {
      onClose();
    }
  };

  return (
    <>
      <Modal
        isOpen={isOpen}
        toggle={handleClose}
        size="lg"
        centered
        backdrop="static"
        keyboard={false}
        data-testid="parametro-modal"
      >
        <ModalHeader toggle={handleClose}>
          <h5 className="modal-title">
            <i className="fas fa-palette me-2"></i>
            {isEdit ? 'Editar Parámetro de Tinta' : 'Agregar Parámetro de Tinta'}
          </h5>
        </ModalHeader>

        <Form onSubmit={handleSubmit}>
          <ModalBody>
            {/* Error Alert */}
            {error && (
              <Alert color="danger" className="mb-3">
                <i className="fas fa-exclamation-triangle me-2"></i>
                {error}
              </Alert>
            )}

            <div className="row">
              {/* Nombre Field */}
              <div className="col-md-6 mb-3">
                <FormGroup>
                  <Label for="nombre" className="form-label">
                    Nombre del Parámetro *
                  </Label>
                  <Input
                    type="text"
                    id="nombre"
                    value={formData.nombre}
                    onChange={(e) => handleInputChange('nombre', e.target.value)}
                    className={`form-control ${errors.nombre ? 'is-invalid' : ''}`}
                    placeholder="Ej: Azul Corporativo"
                    disabled={loading}
                  />
                  {errors.nombre && (
                    <div className="invalid-feedback">
                      {errors.nombre}
                    </div>
                  )}
                </FormGroup>
              </div>

              {/* Tinta Field */}
              <div className="col-md-6 mb-3">
                <FormGroup>
                  <Label for="tinta" className="form-label">
                    Tintas
                  </Label>
                  <select
                    id="tinta"
                    multiple
                    value={formData.tinta}
                    onChange={handleTintaChange}
                    className={`form-control ${errors.tinta ? 'is-invalid' : ''}`}
                    disabled={loading}
                    size={Math.min(tintas.length + 1, 6)} // Show up to 6 options
                  >
                    {sortTintas(tintas).map((tinta) => (
                      <option key={tinta.id} value={tinta.id}>
                        {tinta.nombre}
                      </option>
                    ))}
                  </select>
                  {errors.tinta && (
                    <div className="invalid-feedback">
                      {errors.tinta}
                    </div>
                  )}
                  <small className="form-text text-muted">
                    Mantén presionado Ctrl (Cmd en Mac) para seleccionar múltiples tintas
                  </small>
                </FormGroup>
              </div>
            </div>

            <div className="row">
              {/* Pantone Field */}
              <div className="col-md-8 mb-3">
                <FormGroup>
                  <Label for="pantone" className="form-label">
                    Código Pantone
                  </Label>
                  <div className="input-group">
                  <Input
                      type="text"
                      id="pantone"
                      value={formData.pantone}
                      onChange={(e) => handleInputChange('pantone', e.target.value)}
                      className={`form-control ${errors.pantone ? 'is-invalid' : ''}`}
                    placeholder="Ej: PANTONE 186 C o #FF0000"
                      disabled={loading}
                    />
                    <Button
                      type="button"
                      color="secondary"
                      onClick={() => setShowColorPicker(true)}
                      disabled={loading}
                      className="ms-2"
                    >
                      <i className="fas fa-palette me-1"></i>
                      Seleccionar Color
                    </Button>
                  </div>
                  {errors.pantone && (
                    <div className="invalid-feedback">
                      {errors.pantone}
                    </div>
                  )}
                  <small className="form-text text-muted">
                    Formato: PANTONE 186 C o #FF0000
                  </small>
                </FormGroup>
              </div>

              {/* Estado Field */}
              <div className="col-md-4 mb-3">
                <FormGroup>
                  <Label for="estado" className="form-label">
                    Estado
                  </Label>
                  <Input
                    type="select"
                    id="estado"
                    value={formData.estado ? 'true' : 'false'}
                    onChange={(e) => handleInputChange('estado', e.target.value === 'true')}
                    className="form-control"
                    disabled={loading}
                  >
                    <option value="true">Activo</option>
                    <option value="false">Inactivo</option>
                  </Input>
                </FormGroup>
              </div>
            </div>

            {/* Selected Tintas Preview */}
            {formData.tinta.length > 0 && (
              <div className="row">
                <div className="col-12">
                  <FormGroup>
                    <Label className="form-label">Tintas Seleccionadas</Label>
                    <div className="d-flex flex-wrap gap-2">
                      {formData.tinta
                        .map(tintaId => tintas.find(t => t.id === tintaId))
                        .filter(tinta => tinta !== undefined)
                        .sort((a, b) => {
                          const order = ['C', 'M', 'Y', 'K'];
                          const aIndex = order.indexOf(a!.nombre);
                          const bIndex = order.indexOf(b!.nombre);
                          
                          if (aIndex !== -1 && bIndex !== -1) {
                            return aIndex - bIndex;
                          }
                          if (aIndex !== -1) return -1;
                          if (bIndex !== -1) return 1;
                          return a!.nombre.localeCompare(b!.nombre);
                        })
                        .map((tinta) => (
                          <span key={tinta!.id} className="badge bg-primary">
                            {tinta!.nombre}
                          </span>
                        ))}
                    </div>
                  </FormGroup>
                </div>
              </div>
            )}

            {/* Color Preview */}
            {formData.pantone && (
              <div className="row">
                <div className="col-12">
                  <FormGroup>
                    <Label className="form-label">Vista Previa del Color</Label>
                    <div className="d-flex align-items-center">
                      <div
                        className="color-preview me-3 border rounded"
                        style={{
                          width: '40px',
                          height: '40px',
                          backgroundColor: pantoneHex || '#cccccc',
                        }}
                      />
                      <div>
                        <div className="fw-medium">{formData.pantone}</div>
                        <small className="text-muted">Código Pantone</small>
                      </div>
                    </div>
                  </FormGroup>
                </div>
              </div>
            )}
          </ModalBody>

          <ModalFooter>
            <Button
              type="button"
              color="secondary"
              onClick={handleClose}
              disabled={loading}
            >
              <i className="fas fa-times me-1"></i>
              Cancelar
            </Button>
            <Button
              type="submit"
              color="primary"
              disabled={loading}
            >
              {loading ? (
                <>
                  <Spinner size="sm" className="me-1" />
                  Guardando...
                </>
              ) : (
                <>
                  <i className="fas fa-save me-1"></i>
                  Guardar
                </>
              )}
            </Button>
          </ModalFooter>
        </Form>
      </Modal>

      {/* Pantone Color Picker Modal */}
      <PantoneColorPicker
        isOpen={showColorPicker}
        onColorSelect={handleColorSelect}
        onClose={() => setShowColorPicker(false)}
        selectedColor={formData.pantone ? {
          name: formData.pantone,
          hex: '#cccccc',
          pantone: formData.pantone,
        } : undefined}
      />
    </>
  );
};
